/**
 * VIP等级配置页面
 * 管理VIP等级门槛和权益
 */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Space,
  Tag,
  Typography,
  List,
  Divider,
  Popconfirm,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CrownOutlined,
  SaveOutlined,
  GiftOutlined,
} from '@ant-design/icons'
import { systemConfigApi } from '@/services/api'
import type { VIPLevel } from '@/types/system'

const { Title, Text } = Typography

const VIP_COLORS = ['#999999', '#52c41a', '#1890ff', '#722ed1', '#fa8c16', '#f5222d']

const VIPLevelConfig: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [levels, setLevels] = useState<VIPLevel[]>([])
  const [modalVisible, setModalVisible] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [form] = Form.useForm()

  // 获取VIP等级配置
  const fetchLevels = useCallback(async () => {
    setLoading(true)
    try {
      const response = await systemConfigApi.getVIPLevels()
      const rawLevels = response.data.parsed_value?.levels || response.data.levels || []
      const levelsArray = Array.isArray(rawLevels)
        ? rawLevels.map((value: VIPLevel) => ({
            ...value,
            color: value.color || VIP_COLORS[value.level] || '#1890ff',
            icon: value.icon || 'crown',
            benefits: value.benefits || [],
          }))
        : Object.entries(rawLevels).map(([key, value]: [string, any]) => ({
            level: parseInt(key, 10),
            name: value.name || '',
            threshold: value.threshold || 0,
            color: value.color || VIP_COLORS[parseInt(key, 10)] || '#1890ff',
            icon: value.icon || 'crown',
            benefits: value.benefits || [],
          }))
      // 按等级排序
      levelsArray.sort((a, b) => a.level - b.level)
      setLevels(levelsArray)
    } catch (error: any) {
      message.error(`获取VIP等级配置失败: ${error.message || '未知错误'}`)
    } finally {
      setLoading(false)
    }
  }, [])

  // 初始化
  useEffect(() => {
    fetchLevels()
  }, [fetchLevels])

  // 打开创建模态框
  const handleCreate = () => {
    setEditingIndex(null)
    form.resetFields()
    setModalVisible(true)
  }

  // 打开编辑模态框
  const handleEdit = (record: VIPLevel, index: number) => {
    setEditingIndex(index)
    form.setFieldsValue({
      ...record,
      benefits: record.benefits?.join('\n') || '',
    })
    setModalVisible(true)
  }

  // 删除等级
  const handleDelete = (index: number) => {
    const newLevels = levels.filter((_, i) => i !== index)
    setLevels(newLevels)
    message.success('已删除，请保存更改')
  }

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      const levelData: VIPLevel = {
        level: values.level,
        name: values.name,
        threshold: values.threshold,
        color: values.color,
        icon: values.icon,
        benefits: values.benefits?.split('\n').filter((b: string) => b.trim()) || [],
      }

      let newLevels: VIPLevel[]
      if (editingIndex !== null) {
        newLevels = levels.map((l, i) => (i === editingIndex ? levelData : l))
      } else {
        newLevels = [...levels, levelData]
      }

      // 按等级排序
      newLevels.sort((a, b) => a.level - b.level)

      setLevels(newLevels)
      setModalVisible(false)
      message.success(editingIndex !== null ? '更新成功，请保存更改' : '添加成功，请保存更改')
    } catch (error: any) {
      if (error.errorFields) return
    }
  }

  // 保存到服务器
  const handleSave = async () => {
    if (levels.length === 0) {
      message.error('至少需要配置一个VIP等级')
      return
    }

    setSaving(true)
    try {
      await systemConfigApi.updateVIPLevels({ levels })
      message.success('保存VIP等级配置成功')
      fetchLevels()
    } catch (error: any) {
      message.error(`保存失败: ${error.message || '未知错误'}`)
    } finally {
      setSaving(false)
    }
  }

  // 表格列定义
  const columns = [
    {
      title: '等级',
      dataIndex: 'level',
      key: 'level',
      width: 80,
      render: (level: number) => (
        <Tag
          color={VIP_COLORS[level] || 'default'}
          className="text-base px-3 py-1"
        >
          <CrownOutlined className="mr-1" />
          VIP{level}
        </Tag>
      ),
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '门槛（累计消费）',
      dataIndex: 'threshold',
      key: 'threshold',
      width: 180,
      render: (threshold: number) => (
        <Text strong className="text-orange-500">
          ¥{threshold.toLocaleString()}
        </Text>
      ),
    },
    {
      title: '权益',
      dataIndex: 'benefits',
      key: 'benefits',
      render: (benefits: string[]) => (
        <Space direction="vertical" size="small">
          {benefits?.map((benefit, i) => (
            <Tag key={i} color="blue" className="mr-0">
              <GiftOutlined className="mr-1" />
              {benefit}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: VIPLevel, index: number) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record, index)}
          />
          <Popconfirm
            title="确定删除此等级？"
            onConfirm={() => handleDelete(index)}
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="p-6">
      {/* 页面标题和操作栏 */}
      <Card className="mb-4">
        <Space className="w-full justify-between" wrap>
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <CrownOutlined className="text-yellow-500" />
              VIP等级配置
            </h2>
            <p className="text-gray-500 text-sm">配置VIP等级门槛、权益和特权</p>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchLevels}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              添加等级
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={levels.length === 0}
            >
              保存配置
            </Button>
          </Space>
        </Space>
      </Card>

      {/* VIP等级预览 */}
      <Card className="mb-4" title="VIP等级预览">
        <div className="flex justify-around">
          {levels.length === 0 ? (
            <Text type="secondary" className="py-8">暂无VIP等级配置</Text>
          ) : (
            levels.map((level) => (
              <div
                key={level.level}
                className="text-center p-4 rounded-lg"
                style={{ backgroundColor: `${level.color}20` }}
              >
                <CrownOutlined
                  className="text-3xl mb-2"
                  style={{ color: level.color }}
                />
                <div className="font-bold" style={{ color: level.color }}>
                  {level.name}
                </div>
                <div className="text-sm text-gray-600">
                  ¥{level.threshold.toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* 数据表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={levels}
          rowKey="level"
          loading={loading}
          pagination={false}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* 创建/编辑模态框 */}
      <Modal
        title={editingIndex !== null ? '编辑VIP等级' : '添加VIP等级'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={{
            level: levels.length,
            color: VIP_COLORS[levels.length] || '#1890ff',
            icon: 'crown',
          }}
        >
          <Form.Item
            name="level"
            label="VIP等级"
            rules={[{ required: true, message: '请输入等级数字' }]}
          >
            <InputNumber
              min={0}
              max={10}
              style={{ width: '100%' }}
              placeholder="例如: 1表示VIP1"
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="等级名称"
            rules={[{ required: true, message: '请输入等级名称' }]}
          >
            <Input placeholder="例如: 青铜会员" />
          </Form.Item>

          <Form.Item
            name="threshold"
            label="门槛金额（元）"
            rules={[{ required: true, message: '请输入门槛金额' }]}
            extra="累计消费达到此金额可自动升级"
          >
            <InputNumber<number>
              min={0}
              style={{ width: '100%' }}
              formatter={(value) => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => {
                const parsed = value?.replace(/\¥\s?|(,*)/g, '')
                return parsed ? parseFloat(parsed) : 0
              }}
            />
          </Form.Item>

          <Form.Item
            name="color"
            label="主题颜色"
            rules={[{ required: true, message: '请输入颜色' }]}
          >
            <Input
              type="color"
              style={{ width: '100%', height: 40 }}
            />
          </Form.Item>

          <Form.Item
            name="icon"
            label="图标"
            initialValue="crown"
          >
            <Input disabled placeholder="crown" />
          </Form.Item>

          <Form.Item
            name="benefits"
            label="权益列表"
            extra="每行一个权益"
          >
            <Input.TextArea
              rows={4}
              placeholder={`专属客服\n优先购票\n生日礼包`}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default VIPLevelConfig
